// Replaces the `window.prompt` at TelehealthAdmin.jsx:115-133 (contract §9 pins this
// file's path, default export and the `{appointmentId, open, onClose, gatewayApi}` props
// exactly). The minutes a per-minute consult bills are a SERVER computation
// (`sessions.compute_billable`, gateway-flow-c-design.md §4) — this dialog states them,
// it never asks a human to type them. No editable minutes field exists anywhere in this
// tree; the test workstream asserts that absence directly.
//
// Three actions, each a distinct server call: Complete (bills the evidenced minutes),
// No-show (flat platform fee, needs which party didn't show), Cancel (releases the hold).
// Zero native popups — Radix `Dialog` only, and the no-show party choice is a Radix
// `RadioGroup`, never `window.prompt`/`confirm`.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, UserX, Ban } from "lucide-react";
// Relative: new gateway modules, no existing "@/" Jest shim — see
// gateway/__testutils__/mocks.js for why this project's convention is to resolve these
// for real rather than mock them.
import { fmtMoney } from "../money";
import defaultGatewayApi from "../../gateway/api";
import { useConsultSession } from "../../gateway/hooks/useConsultSession";
import { extractGatewayErrorCode } from "../../gateway/errors";
import { gatewayErrorText } from "../../gateway/errorText";
import { SETTLEMENT_CURRENCY } from "../../gateway/contract";
import SessionTimeline from "./SessionTimeline";

/**
 * @param {string} appointmentId
 * @param {boolean} open
 * @param {function} onClose (didMutate: boolean) => void
 * @param {object} [gatewayApi] DI seam (contract §9)
 */
export default function ConsultCompleteDialog({ appointmentId, open, onClose, gatewayApi = defaultGatewayApi }) {
  const t = useT("admin");
  const tc = useT("common");
  const { data: session, error: sessionError,
          reload: reloadSession } = useConsultSession(appointmentId, {}, gatewayApi);

  // THE CONSULT WHOSE ROOM WAS NEVER OPENED (finding F-06). `provision` mints the session
  // when somebody enters the room, so an authorised appointment nobody entered has none —
  // and `GET /appointments/{id}/session` answers 404 PAYMENT_NOT_FOUND for it. There are
  // no minutes to bill and no no-show to record without a session (both calls would 409),
  // but the card may be HELD, and releasing that hold is exactly what this dialog is for.
  // So the two actions that need a session are withheld and Cancel is offered alone.
  //
  // On the CODE and not on `!session`: a network failure or an expired admin session must
  // not be read as "this consultation has no room", which would hide the completion of a
  // consultation that really happened.
  const noSession = extractGatewayErrorCode(sessionError) === "PAYMENT_NOT_FOUND";

  // step: "confirm" (three actions) | "noshow-party" (pick who) | "computed" (breakdown,
  // read-only) | "cancelled"
  const [step, setStep] = useState("confirm");
  const [party, setParty] = useState("patient");
  const [busy, setBusy] = useState(false);
  const [errorCode, setErrorCode] = useState(null);
  const [computation, setComputation] = useState(null);

  useEffect(() => {
    if (open) {
      setStep("confirm");
      setParty("patient");
      setErrorCode(null);
      setComputation(null);
      if (appointmentId) reloadSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, appointmentId]);

  const runComplete = async () => {
    setBusy(true);
    setErrorCode(null);
    try {
      const result = await gatewayApi.completeConsult(appointmentId);
      setComputation(result);
      setStep("computed");
      // THE EMBEDDED TIMELINE IS NOW STALE, and it is stale in the one way that
      // contradicts what is about to be rendered underneath it: `SessionTimeline` prints
      // "the consultation is still running" whenever `computation.computedBy` is absent,
      // which on the read taken when this dialog opened it always is. Photographed on
      // 2026-08-28 directly above a finished breakdown. The failure path below already
      // reloaded; the success path did not.
      reloadSession();
    } catch (e) {
      // ALREADY_COMPLETED echoes the stored computation server-side (contract §3); the
      // embedded SessionTimeline above already reflects that stored state once reloaded,
      // so surfacing the code here — rather than guessing at a mismatched field shape —
      // is what tells the practitioner where to look, without fabricating a breakdown.
      setErrorCode(extractGatewayErrorCode(e));
      reloadSession();
    } finally {
      setBusy(false);
    }
  };

  const runNoShow = async () => {
    setBusy(true);
    setErrorCode(null);
    try {
      const result = await gatewayApi.noShowConsult(appointmentId, party);
      setComputation(result);
      setStep("computed");
      // A no-show decides money too, and lands on the same "computed" step.
      reloadSession();
    } catch (e) {
      setErrorCode(extractGatewayErrorCode(e));
    } finally {
      setBusy(false);
    }
  };

  const runCancel = async () => {
    setBusy(true);
    setErrorCode(null);
    try {
      await gatewayApi.cancelAppointment(appointmentId);
      toast.success(t("consult.complete.cancelled"));
      onClose(true);
    } catch (e) {
      setErrorCode(extractGatewayErrorCode(e));
    } finally {
      setBusy(false);
    }
  };

  // `session.currency` does not exist on the wire: timeline_view nests it under
  // `computation` (consults.computation_response). Both fallbacks now name a real field.
  const currency = computation?.currency || session?.computation?.currency
    || SETTLEMENT_CURRENCY;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(step === "computed"); }}>
      <DialogContent data-testid="consult-complete-dialog">
        <DialogHeader>
          <DialogTitle>{t("consult.complete.title")}</DialogTitle>
          <DialogDescription>{t("consult.complete.desc")}</DialogDescription>
        </DialogHeader>

        <SessionTimeline session={session} />

        {errorCode && (
          <p className="text-xs text-rose-600" role="alert" data-testid="consult-complete-error">
            {gatewayErrorText(errorCode, tc)}
          </p>
        )}

        {step === "confirm" && noSession && (
          <p className="text-xs text-eh-muted" data-testid="consult-complete-never-opened">
            {t("consult.complete.neverOpened")}
          </p>
        )}

        {step === "confirm" && (
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={runCancel} disabled={busy} data-testid="consult-complete-cancel-action">
              <Ban className="w-4 h-4 mr-1.5" /> {t("consult.complete.actionCancel")}
            </Button>
            {!noSession && (
              <Button variant="outline" onClick={() => setStep("noshow-party")} disabled={busy} data-testid="consult-complete-noshow-action">
                <UserX className="w-4 h-4 mr-1.5" /> {t("consult.complete.actionNoShow")}
              </Button>
            )}
            {!noSession && (
              <Button onClick={runComplete} disabled={busy} data-testid="consult-complete-action">
                {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> {t("consult.complete.actionComplete")}
              </Button>
            )}
          </DialogFooter>
        )}

        {step === "noshow-party" && (
          <>
            <div className="space-y-2">
              <Label className="text-xs text-eh-muted">{t("consult.complete.noShowWho")}</Label>
              <RadioGroup value={party} onValueChange={setParty} data-testid="consult-noshow-party">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <RadioGroupItem value="patient" data-testid="consult-noshow-party-patient" /> {t("consult.complete.noShowPatient")}
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <RadioGroupItem value="practitioner" data-testid="consult-noshow-party-practitioner" /> {t("consult.complete.noShowPractitioner")}
                </label>
              </RadioGroup>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep("confirm")} disabled={busy}>{t("consult.complete.back")}</Button>
              <Button onClick={runNoShow} disabled={busy} data-testid="consult-noshow-confirm">
                {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} {t("consult.complete.noShowConfirm")}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "computed" && computation && (
          <>
            <div className="rounded-xl bg-eh-cream px-4 py-4 space-y-1.5 text-sm" data-testid="consult-complete-breakdown">
              <div className="flex justify-between">
                <span className="text-eh-muted">{t("consult.complete.billableMinutes")}</span>
                <span className="font-semibold tabular-nums" data-testid="consult-complete-minutes">{computation.billableMinutes}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-eh-muted">{t("consult.complete.gross")}</span>
                <span className="font-medium tabular-nums">{fmtMoney(computation.grossCents, currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-eh-muted">{t("consult.complete.commission")}</span>
                <span className="font-medium tabular-nums text-rose-600">{fmtMoney(computation.commissionCents, currency)}</span>
              </div>
              <div className="flex justify-between border-t border-eh-border-soft pt-1.5">
                <span className="text-eh-muted">{t("consult.complete.net")}</span>
                <span className="font-semibold tabular-nums text-emerald-700">{fmtMoney(computation.netCents, currency)}</span>
              </div>
              {computation.releasedCents > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-eh-muted">{t("consult.complete.released")}</span>
                  <span className="tabular-nums">{fmtMoney(computation.releasedCents, currency)}</span>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={() => onClose(true)} data-testid="consult-complete-done">{t("consult.complete.done")}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
